'use client';

import { useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  buildReadmeMarkdown,
  renderMarkdownToHtml,
  type ReadmeSections,
} from '@/lib/browser-markdown-tools';

const initialSections: ReadmeSections = {
  projectName: '',
  shortDescription: '',
  features: '',
  installation: '',
  usage: '',
  configuration: '',
  scripts: '',
  license: '',
  links: '',
};

function createTextDownload(text: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error('The browser could not access the clipboard.');
}

export default function BrowserReadmeBuilder() {
  const [sections, setSections] = useState<ReadmeSections>(initialSections);
  const [resultMarkdown, setResultMarkdown] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  function updateSection(key: keyof ReadmeSections, value: string) {
    setSections((current) => ({ ...current, [key]: value }));
    setResultMarkdown(null);
    setPreviewHtml(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);
  }

  async function generateReadme() {
    if (!Object.values(sections).some((value) => value.trim())) {
      setErrorMessage('Add at least a project name or one README section first.');
      return;
    }

    setIsRunning(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);

    try {
      const credits = await consumeToolUsage(
        'readme-builder',
        'Credits are not available for another README build right now.',
      );
      const markdown = buildReadmeMarkdown(sections);
      const html = await renderMarkdownToHtml(markdown);
      setResultMarkdown(markdown);
      setPreviewHtml(html);
      setCreditMessage(formatToolCreditSuccess('README generation', credits));
    } catch (error) {
      setResultMarkdown(null);
      setPreviewHtml(null);
      setErrorMessage(
        error instanceof Error ? error.message : 'The README could not be generated.',
      );
    } finally {
      setIsRunning(false);
    }
  }

  async function handleCopy() {
    if (!resultMarkdown) {
      return;
    }

    try {
      await copyText(resultMarkdown);
      setCopyMessage('README Markdown copied.');
      setErrorMessage(null);
    } catch {
      setErrorMessage('The browser could not copy the current README.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">README</span>
        <h2 className="tool-live-title">Build a practical README from common project sections</h2>
        <p className="tool-live-subtitle">
          Fill the core sections, assemble one clean <code>README.md</code>, and review the rendered
          output locally.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-markdown-readme-grid">
            <label className="tool-live-field tool-live-field-full">
              <span>Project name</span>
              <input
                onChange={(event) => updateSection('projectName', event.target.value)}
                placeholder="Cleanor Markdown Toolkit"
                type="text"
                value={sections.projectName}
              />
            </label>

            <label className="tool-live-field tool-live-field-full">
              <span>Short description</span>
              <textarea
                onChange={(event) => updateSection('shortDescription', event.target.value)}
                placeholder="Browser-first Markdown utilities for previews, conversions, and README cleanup."
                value={sections.shortDescription}
              />
            </label>

            <label className="tool-live-field">
              <span>Features</span>
              <textarea
                onChange={(event) => updateSection('features', event.target.value)}
                placeholder={'- Live GFM preview\n- HTML conversion\n- Table tools'}
                value={sections.features}
              />
            </label>

            <label className="tool-live-field">
              <span>Installation</span>
              <textarea
                onChange={(event) => updateSection('installation', event.target.value)}
                placeholder={'```bash\nnpm install\n```'}
                value={sections.installation}
              />
            </label>

            <label className="tool-live-field">
              <span>Usage</span>
              <textarea
                onChange={(event) => updateSection('usage', event.target.value)}
                placeholder="Explain the main workflow or entry command here."
                value={sections.usage}
              />
            </label>

            <label className="tool-live-field">
              <span>Configuration</span>
              <textarea
                onChange={(event) => updateSection('configuration', event.target.value)}
                placeholder="List important env vars or config options."
                value={sections.configuration}
              />
            </label>

            <label className="tool-live-field">
              <span>Scripts / commands</span>
              <textarea
                onChange={(event) => updateSection('scripts', event.target.value)}
                placeholder={'```bash\nnpm run build\nnpm run lint\n```'}
                value={sections.scripts}
              />
            </label>

            <label className="tool-live-field">
              <span>License</span>
              <textarea
                onChange={(event) => updateSection('license', event.target.value)}
                placeholder="MIT"
                value={sections.license}
              />
            </label>

            <label className="tool-live-field tool-live-field-full">
              <span>Links</span>
              <textarea
                onChange={(event) => updateSection('links', event.target.value)}
                placeholder={
                  '- [Docs](https://example.com/docs)\n- [Issues](https://example.com/issues)'
                }
                value={sections.links}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button"
              disabled={isRunning}
              onClick={() => void generateReadme()}
              type="button"
            >
              {isRunning ? 'Building…' : 'Generate README'}
            </button>
          </div>

          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="status-message status-success">{creditMessage}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <aside className="tool-live-panel">
          {resultMarkdown ? (
            <div className="tool-live-actions">
              <button
                className="button button-secondary"
                onClick={() => void handleCopy()}
                type="button"
              >
                Copy README
              </button>
              <button
                className="button button-secondary"
                onClick={() => createTextDownload(resultMarkdown, 'README.md')}
                type="button"
              >
                Download README.md
              </button>
            </div>
          ) : null}

          {resultMarkdown ? (
            <div className="tool-live-preview">
              <h3>Markdown output</h3>
              <pre className="tool-markdown-code">{resultMarkdown}</pre>
            </div>
          ) : null}

          {previewHtml ? (
            <div className="tool-live-preview">
              <h3>Rendered preview</h3>
              <div
                className="tool-markdown-rendered"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
